import type { GameState, Incident } from '@full-elevator/sim';
import { incidents } from '@full-elevator/sim';
import type { Graphics } from 'pixi.js';
import { COLORS, SIRENS } from '../theme.js';

/**
 * What an emergency looks like while it is under way (ADR-0029).
 *
 * The owner's ask: when a crew arrives, the light changes, until they have
 * gone — so the player knows it is urgent and goes first. Three things carry
 * it, none of them a ticket's colour:
 *
 * - the building's outside flashes in the crew's colours (red and white for an
 *   ambulance, red and blue for the police), the way a street does when one is
 *   parked outside;
 * - a beacon on the incident's floor, with a ring that fills while the crew is
 *   in there and the return is not ready yet;
 * - a siren mark on the crew's ticket, so it reads without colour at all.
 *
 * View only: who is where, and when the return will be ready, are the sim's
 * (`incidents`). Under reduced motion every light holds still.
 */

export type CrewKind = 'ambulance' | 'police';

export function crewKind(incident: Incident): CrewKind {
  return incident.crew.archetype === 'police' ? 'police' : 'ambulance';
}

/** The two colours a crew flashes, in turn. */
export function sirenPair(kind: CrewKind): readonly [number, number] {
  return kind === 'police' ? [SIRENS.red, SIRENS.blue] : [SIRENS.red, SIRENS.white];
}

/** Which of the pair is lit at this moment: every 420ms, or the first one, still. */
export function sirenPhase(ms: number, still: boolean): 0 | 1 {
  return still ? 0 : Math.floor(ms / 420) % 2 === 0 ? 0 : 1;
}

/** Every emergency under way, with what kind of crew it is. */
export function underWay(state: GameState): { incident: Incident; kind: CrewKind }[] {
  return incidents(state).map((incident) => ({ incident, kind: crewKind(incident) }));
}

/**
 * A beacon on the incident's floor: a light bar on the ceiling, its two lamps
 * taking turns, and a soft cone of the lit one's colour below it.
 */
export function paintBeacon(
  g: Graphics,
  x: number,
  y: number,
  kind: CrewKind,
  ms: number,
  still: boolean,
): void {
  const pair = sirenPair(kind);
  const lit = sirenPhase(ms, still);
  const glow = pair[lit] as number;
  g.moveTo(x - 6, y + 4)
    .lineTo(x - 26, y + 34)
    .lineTo(x + 26, y + 34)
    .lineTo(x + 6, y + 4)
    .closePath()
    .fill({ color: glow, alpha: still ? 0.12 : 0.2 });
  g.roundRect(x - 13, y - 2, 26, 7, 3.5)
    .fill({ color: COLORS.cabin })
    .stroke({ width: 1.2, color: COLORS.ink });
  g.roundRect(x - 11, y - 0.5, 10, 4, 2).fill({ color: pair[0], alpha: lit === 0 ? 1 : 0.35 });
  g.roundRect(x + 1, y - 0.5, 10, 4, 2).fill({ color: pair[1], alpha: lit === 1 ? 1 : 0.35 });
}

/** While the crew is in there: a ring that fills until the return is ready. */
export function paintReadyRing(g: Graphics, x: number, y: number, done: number): void {
  const r = 8;
  g.circle(x, y, r).fill({ color: COLORS.cabin }).stroke({ width: 1.5, color: COLORS.ink });
  const end = -Math.PI / 2 + Math.PI * 2 * Math.min(1, Math.max(0, done));
  if (done > 0.01) {
    g.moveTo(x, y)
      .arc(x, y, r - 2.2, -Math.PI / 2, end)
      .closePath()
      .fill({ color: COLORS.ink, alpha: 0.75 });
  }
}

/**
 * The siren mark on a crew's ticket, at the top edge's middle: a dome and its
 * rays, the rays taking turns in the crew's colours unless held still.
 */
export function paintSiren(
  g: Graphics,
  x: number,
  y: number,
  kind: CrewKind,
  ms: number,
  still: boolean,
): void {
  const pair = sirenPair(kind);
  const lit = pair[sirenPhase(ms, still)] as number;
  for (const side of [-1, 1]) {
    g.moveTo(x + side * 6.5, y - 1)
      .lineTo(x + side * 9.5, y - 3)
      .stroke({ width: 1.6, color: lit, cap: 'round' });
  }
  g.moveTo(x - 5, y + 1)
    .lineTo(x - 5, y - 2)
    .arc(x, y - 2, 5, Math.PI, 0)
    .lineTo(x + 5, y + 1)
    .closePath()
    .fill({ color: lit })
    .stroke({ width: 1.2, color: COLORS.ink });
  g.rect(x - 6.5, y + 1, 13, 2.2).fill({ color: COLORS.ink });
}
